import {
  closestCenter,
  DndContext,
  DragOverlay,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { isSameDay } from 'date-fns'
import { Check, GripVertical, Trash2Icon } from 'lucide-react'
import { useState } from 'react'

import { workflowTaskSchema } from '@/app/(app)/idea-management/challenge-management/schema'
import { Button, DatePicker, Input, Separator } from '@/components/ui'

import { useTaskDragDrop } from './hooks/useTaskDragDrop'
import { InviteResponsibleModal } from './inviteResponsibleModal/inviteResponsibleModal'

import type {
  WorkflowGateTask,
  WorkflowGateTaskResponsible,
} from './workflowGate'

export function WorkflowTaskViewHeader({
  enableResponsible = true,
  enableEndDate = true,
}: {
  enableResponsible?: boolean
  enableEndDate?: boolean
}) {
  return (
    <div className="flex flex-col gap-2 px-12 py-2">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-system-text-primary">
            Tarefa
          </span>
        </div>
        {(enableResponsible || enableEndDate) && (
          <div className="flex items-center gap-6">
            {enableResponsible && (
              <span className="text-sm font-medium text-system-text-primary">
                Responsável
              </span>
            )}
            {enableEndDate && (
              <span className="text-sm font-medium text-system-text-primary">
                Prazo
              </span>
            )}
          </div>
        )}
      </header>
    </div>
  )
}

type TaskItemContentProps = {
  task: WorkflowGateTask
  taskIndex: number
  isEditing: boolean
  taskError: { index: number; message: string } | null
  onStartEdit: () => void
  onUpdateTask: (taskIndex: number, name: string) => void
  onDeleteTask: (taskIndex: number) => void
  onUpdateTaskEndDate?: (taskIndex: number, endDate: Date | undefined) => void
  onUpdateTaskResponsible?: (
    taskIndex: number,
    responsible: WorkflowGateTaskResponsible | undefined,
  ) => void
  enableResponsible?: boolean
  enableEndDate?: boolean
  onSaveTask: (taskIndex: number) => void
  onCancelEdit: () => void
  setTaskError: (error: { index: number; message: string } | null) => void
  areaIds?: string[]
  renderDragHandle?: () => React.ReactNode
}

function TaskItemContent({
  task,
  taskIndex,
  isEditing,
  taskError,
  onStartEdit,
  onUpdateTask,
  onDeleteTask,
  onUpdateTaskEndDate,
  onUpdateTaskResponsible,
  enableResponsible = true,
  enableEndDate = true,
  onSaveTask,
  onCancelEdit,
  setTaskError,
  areaIds,
  renderDragHandle,
}: TaskItemContentProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full items-start">
        {isEditing ? (
          <div className="flex w-full items-start gap-2">
            <div className="flex w-full items-start gap-2 px-4">
              {renderDragHandle && (
                <div className="flex h-10 shrink-0 items-center text-sm font-medium text-system-text-primary">
                  {renderDragHandle()}
                </div>
              )}
              <div className="flex w-full flex-col gap-1">
                <Input
                  value={task.name}
                  onChange={(e) => {
                    onUpdateTask(taskIndex, e.target.value)
                    if (taskError?.index === taskIndex) setTaskError(null)
                  }}
                  className="w-full border-system-primary-purple"
                />
                {taskError?.index === taskIndex && (
                  <span className="text-xs text-system-state-error">
                    {taskError.message}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="social"
                onClick={() => onSaveTask(taskIndex)}
              >
                <Check className="size-4" />
                Salvar
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-system-state-error"
                onClick={onCancelEdit}
              >
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2 px-4">
              {renderDragHandle && (
                <div className="w-fit text-sm font-medium text-system-text-primary">
                  {renderDragHandle()}
                </div>
              )}
              <span
                className="w-full cursor-pointer rounded-lg p-2 text-base text-system-text-secondary transition-colors hover:border hover:border-system-primary-purple"
                onClick={onStartEdit}
              >
                {task.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {enableResponsible && onUpdateTaskResponsible && (
                <InviteResponsibleModal
                  taskIndex={taskIndex}
                  onChange={(value) => {
                    onUpdateTaskResponsible(taskIndex, value ?? undefined)
                  }}
                  responsible={task.responsible ?? null}
                  disabled={false}
                  areaIds={areaIds}
                />
              )}
              {enableEndDate && onUpdateTaskEndDate && (
                <div>
                  <DatePicker
                    className="w-fit bg-transparent transition-colors hover:bg-system-background-base"
                    date={task.endDate}
                    onSelect={(date) => {
                      const isSameSelectedDay =
                        date && task.endDate
                          ? isSameDay(date, task.endDate)
                          : false
                      const nextDate = isSameSelectedDay
                        ? undefined
                        : (date ?? undefined)

                      onUpdateTaskEndDate(taskIndex, nextDate)
                    }}
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-system-state-error"
                  onClick={() => onDeleteTask(taskIndex)}
                >
                  <Trash2Icon className="size-5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Separator />
    </div>
  )
}

TaskItemContent.displayName = 'TaskItemContent'

type SortableTaskItemProps = Omit<TaskItemContentProps, 'renderDragHandle'> & {
  id: UniqueIdentifier
}

function SortableTaskItem({ id, ...contentProps }: SortableTaskItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const renderDragHandle = () => (
    <div {...attributes} {...listeners} className="cursor-move">
      <GripVertical className="size-6 text-system-primary-icon" />
    </div>
  )

  return (
    <div ref={setNodeRef} style={style}>
      <TaskItemContent {...contentProps} renderDragHandle={renderDragHandle} />
    </div>
  )
}

SortableTaskItem.displayName = 'SortableTaskItem'

type TaskDragOverlayProps = {
  task: WorkflowGateTask
}

function TaskDragOverlay({ task }: TaskDragOverlayProps) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-system-primary-purple bg-system-background-base shadow-lg">
      <div className="flex w-full items-center justify-between gap-2 p-2">
        <div className="flex w-full items-center gap-2 px-4">
          <div className="w-fit cursor-move text-sm font-medium text-system-text-primary">
            <GripVertical className="size-6 text-system-primary-icon" />
          </div>
          <span className="w-full rounded-lg p-2 text-base text-system-text-secondary">
            {task.name}
          </span>
        </div>
      </div>
    </div>
  )
}

type WorkflowTaskViewProps = {
  tasks: WorkflowGateTask[]
  onUpdateTask: (taskIndex: number, name: string) => void
  onDeleteTask: (taskIndex: number) => void
  onUpdateTaskEndDate?: (taskIndex: number, endDate: Date | undefined) => void
  onUpdateTaskResponsible?: (
    taskIndex: number,
    responsible: WorkflowGateTaskResponsible | undefined,
  ) => void
  onReorderTasks: (reorderedTasks: WorkflowGateTask[]) => void
  areaIds?: string[]
  disableDragDrop?: boolean
  enableResponsible?: boolean
  enableEndDate?: boolean
}

export function WorkflowTaskView({
  tasks,
  onUpdateTask,
  onDeleteTask,
  onUpdateTaskEndDate,
  onUpdateTaskResponsible,
  onReorderTasks,
  areaIds,
  disableDragDrop = false,
  enableResponsible = true,
  enableEndDate = true,
}: WorkflowTaskViewProps) {
  const showResponsible = enableResponsible && onUpdateTaskResponsible != null
  const showEndDate = enableEndDate && onUpdateTaskEndDate != null
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null)
  const [taskError, setTaskError] = useState<{
    index: number
    message: string
  } | null>(null)

  const {
    dndContextId,
    sensors,
    itemIds,
    activeItem,
    handleDragStart,
    handleDragEnd,
  } = useTaskDragDrop({
    tasks,
    onReorder: onReorderTasks,
  })

  function handleSaveTask(taskIndex: number) {
    const task = tasks[taskIndex]
    if (!task) return
    const trimmedName = task.name.trim()
    const result = workflowTaskSchema.safeParse({
      id: task.id,
      name: trimmedName,
    })
    if (!result.success) {
      const message = result.error.issues[0]?.message ?? 'Nome inválido'
      setTaskError({ index: taskIndex, message })
      return
    }
    if (trimmedName !== task.name) onUpdateTask(taskIndex, trimmedName)
    setTaskError(null)
    setEditingTaskIndex(null)
  }

  return (
    <div className="flex flex-col gap-4">
      <WorkflowTaskViewHeader
        enableResponsible={showResponsible}
        enableEndDate={showEndDate}
      />
      <Separator />
      <div className="flex flex-col gap-2">
        {tasks.length === 0 ? (
          <span className="text-sm text-system-text-secondary">
            Nenhuma tarefa neste estágio.
          </span>
        ) : disableDragDrop ? (
          tasks.map((task, taskIndex) => (
            <TaskItemContent
              key={task.id ?? taskIndex}
              task={task}
              taskIndex={taskIndex}
              isEditing={editingTaskIndex === taskIndex}
              taskError={taskError}
              onStartEdit={() => setEditingTaskIndex(taskIndex)}
              onUpdateTask={onUpdateTask}
              onDeleteTask={onDeleteTask}
              onUpdateTaskEndDate={onUpdateTaskEndDate}
              onUpdateTaskResponsible={onUpdateTaskResponsible}
              enableResponsible={showResponsible}
              enableEndDate={showEndDate}
              onSaveTask={handleSaveTask}
              onCancelEdit={() => {
                setTaskError(null)
                setEditingTaskIndex(null)
              }}
              setTaskError={setTaskError}
              areaIds={areaIds}
            />
          ))
        ) : (
          <DndContext
            id={dndContextId}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={itemIds}
              strategy={verticalListSortingStrategy}
            >
              {tasks.map((task, taskIndex) => (
                <SortableTaskItem
                  key={itemIds[taskIndex]}
                  id={itemIds[taskIndex]}
                  task={task}
                  taskIndex={taskIndex}
                  isEditing={editingTaskIndex === taskIndex}
                  taskError={taskError}
                  onStartEdit={() => setEditingTaskIndex(taskIndex)}
                  onUpdateTask={onUpdateTask}
                  onDeleteTask={onDeleteTask}
                  onUpdateTaskEndDate={onUpdateTaskEndDate}
                  onUpdateTaskResponsible={onUpdateTaskResponsible}
                  enableResponsible={showResponsible}
                  enableEndDate={showEndDate}
                  onSaveTask={handleSaveTask}
                  onCancelEdit={() => {
                    setTaskError(null)
                    setEditingTaskIndex(null)
                  }}
                  setTaskError={setTaskError}
                  areaIds={areaIds}
                />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeItem ? <TaskDragOverlay task={activeItem} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  )
}
